<template>
    <div class="left-panel">
        <div class="left-panel-header">
            <div class="left-panel-header-title">
                <span>NOMINATION SESSIONS</span>
            </div>
            <div class="left-panel-header-option">
                <b-button type="is-primary" outlined size="is-small" @click="refreshSessions">
                    <b-icon icon="refresh" size="is-small"></b-icon>
                </b-button>
            </div>
        </div>
        <div class="left-panel-body">
            <div v-if="loading" class="loading-sessions">
                <div class="loading-content">
                    <b-loading :is-full-page="false" v-model="loading"></b-loading>
                    <p>Loading sessions...</p>
                </div>
            </div>

            <div v-else-if="nominationSessions && nominationSessions.length > 0">
                <div class="nomination-session-item" v-for="(session, index) in nominationSessions" :key="index"
                    :class="{ active: selectedSession && selectedSession.id == session.id }"
                    @click="selectSession(session)">
                    <div class="nomination-session-item-cover">
                        <img v-if="session.cover_image"
                            :src="$store.state.ASSETS_BASE_URL + '/assets/uploaded/' + session.cover_image" />
                        <div v-else class="no-image">
                            <b-icon icon="image" size="is-large"></b-icon>
                        </div>
                    </div>

                    <div class="nomination-session-item-content">
                        <div class="nomination-session-title">{{ session.title }}</div>
                        <div class="nomination-session-meta">
                            <div class="nomination-session-phase">
                                <b-tag :type="getPhaseType(session.phase)" size="is-small">
                                    {{ getPhaseText(session.phase) }}
                                </b-tag>
                            </div>
                            <div class="nomination-session-dates">
                                <small>Ends: {{ formatDate(session.end_time) }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="nomination-session-item-footer">
                        <div class="nomination-session-status">
                            <b-tag v-if="session.status == 'public'" type="is-success" size="is-small">
                                Public
                            </b-tag>
                            <b-tag v-if="session.status == 'unlisted'" type="is-danger" size="is-small">
                                Unlisted
                            </b-tag>
                        </div>
                        <div class="nomination-session-options">
                            <b-dropdown aria-role="list" position="is-bottom-left" size="is-small">
                                <template #trigger>
                                    <b-icon icon="dots-vertical" size="is-small"></b-icon>
                                </template>
                                <b-dropdown-item @click="openSession(session)" aria-role="listitem">
                                    <b-icon icon="external-link-alt" size="is-small"></b-icon>
                                    Open Session
                                </b-dropdown-item>
                                <b-dropdown-item @click="duplicateSession(session)" aria-role="listitem">
                                    <b-icon icon="copy" size="is-small"></b-icon>
                                    Duplicate
                                </b-dropdown-item>
                                <b-dropdown-item @click="updateSession(session)" aria-role="listitem">
                                    <b-icon icon="edit" size="is-small"></b-icon>
                                    Update
                                </b-dropdown-item>
                                <b-dropdown-item @click="deleteSession(session)" aria-role="listitem">
                                    <b-icon icon="trash" size="is-small"></b-icon>
                                    Delete
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="no-sessions">
                <div class="no-sessions-content">
                    <b-icon icon="calendar-alt" size="is-large"></b-icon>
                    <h4>No Nomination Sessions</h4>
                    <p>You haven't created any nomination sessions yet.</p>
                    <b-button type="is-primary" size="is-small" @click="$emit('create-session')">
                        Create First Session
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    components: {},
    props: {
        nominationSessions: {
            type: Array,
            default: () => []
        },
        selectedSession: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({}),
    mounted() {
        console.log('NominationsSessions mounted with sessions:', this.nominationSessions);
    },
    watch: {
        nominationSessions: {
            handler(newSessions) {
                console.log('NominationsSessions received sessions:', newSessions);
            },
            immediate: true
        }
    },
    methods: {
        selectSession(session) {
            this.$emit("session-selected", session);
        },
        formatDate(dateString) {
            if (!dateString) return '';
            return this.moment(dateString).format('MMM DD, YYYY');
        },
        refreshSessions() {
            this.$emit("refresh-sessions");
        },
        openSession(session) {
            // Open session in new tab or navigate to session viewer
            const url = `/nomination/${session.slug}`;
            window.open(url, '_blank');
        },
        duplicateSession(session) {
            this.$buefy.dialog.confirm({
                title: "Duplicate Session",
                message: `Are you sure you want to duplicate "${session.title}"?`,
                confirmText: "Duplicate",
                type: "is-info",
                hasIcon: true,
                onConfirm: () => {
                    this.$emit("duplicate-session", session);
                },
            });
        },
        updateSession(session) {
            this.$emit("update-session", session);
        },
        deleteSession(session) {
            this.$buefy.dialog.confirm({
                title: "Delete Session",
                message: `Are you sure you want to delete "${session.title}"? This action cannot be undone.`,
                confirmText: "Delete",
                type: "is-danger",
                hasIcon: true,
                onConfirm: () => {
                    this.$emit("delete-session", session);
                },
            });
        },
        getPhaseType(phase) {
            switch (phase) {
                case 'nomination':
                    return 'is-info';
                case 'voting':
                    return 'is-success';
                default:
                    return 'is-light';
            }
        },
        getPhaseText(phase) {
            switch (phase) {
                case 'nomination':
                    return 'Nomination Phase';
                case 'voting':
                    return 'Voting Phase';
                default:
                    return 'Unknown Phase';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.left-panel {
    height: 100%;
    width: 400px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    border-right: 1px solid #ddd;
    background: #fff;

    @media (max-width: 592px) {
        position: absolute;
        background: white;
        z-index: 10;
        top: 60px;
        left: 0;
        width: 85%;
        box-shadow: 10px 10px 10px rgba(0, 0, 0, 0.089);
    }

    @media (max-width: 470px) {
        width: 96%;
    }

    .left-panel-header {
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #ddd;
        padding: 0px 10px;
        background: #fff;
        position: sticky;
        top: 0;
        z-index: 1;

        .left-panel-header-title {
            font-size: 14px;
            font-weight: 600;
        }

        .left-panel-header-option {
            display: flex;
            align-items: center;
        }
    }

    .left-panel-body {
        overflow-y: auto;
        height: calc(100% - 60px);

        .nomination-session-item {
            width: 100%;
            display: flex;
            flex-direction: column;
            border-bottom: 1px solid #ddd;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
                background: #f9f9f9;
            }

            &.active {
                background: #f0f8e7;
                border-left: 4px solid #9fc953;
            }

            .nomination-session-item-cover {
                height: 100px;
                width: 100%;
                overflow: hidden;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .no-image {
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f5f5f5;
                    color: #ccc;
                }
            }

            .nomination-session-item-content {
                padding: 10px;

                .nomination-session-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 8px;
                    line-height: 1.3;
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }

                .nomination-session-meta {
                    .nomination-session-phase {
                        margin-bottom: 5px;
                    }

                    .nomination-session-dates {
                        small {
                            color: #666;
                            font-size: 11px;
                        }
                    }
                }
            }

            .nomination-session-item-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 10px;
                background: #f8f9fa;

                .nomination-session-status {
                    display: flex;
                    align-items: center;
                }

                .nomination-session-options {
                    .dropdown {
                        .dropdown-trigger {
                            .button {
                                border: none;
                                background: transparent;
                                padding: 5px;

                                &:hover {
                                    background: rgba(0, 0, 0, 0.1);
                                }
                            }
                        }
                    }
                }
            }
        }

        .no-sessions {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 300px;

            .no-sessions-content {
                text-align: center;
                color: #666;

                .icon {
                    color: #ccc;
                    margin-bottom: 15px;
                }

                h4 {
                    margin: 0 0 10px 0;
                    color: #999;
                }

                p {
                    margin: 0 0 20px 0;
                    font-size: 14px;
                }
            }

            .loading-sessions {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 200px;
                position: relative;

                .loading-content {
                    text-align: center;
                    color: #666;

                    p {
                        margin-top: 20px;
                        font-size: 14px;
                    }
                }
            }
        }
    }
}
</style>
